import { Color, Viewer, type Entity, CustomDataSource } from 'cesium';
import { Cartesian3 } from 'cesium';
import cloneDeep from 'lodash.clonedeep';
import { derived, writable, type Readable } from 'svelte/store';
import {
	deactivateAddMastpointTool,
	removeAllDrawnPolylineDragHandlesForEditablePolyline,
	removeAllScreenSpaceInputActions,
	toggleAddMarkerEntityToMap,
	toggleAddBoxEntityToMap,
	toggleAddLabelEntityToMap,
	toggleRemoveEntities,
	toggleSelectPolesForTerrainProfileCreation,
	deactivatePolygonDrawingTool,
	toggleEditDrawnEntity,
	toggleDrawRectangleOnMap,
	toggleEntityTypesToMap,
	//toggleMoveMastPointEntityInMap
} from './map-tools';
import { deactivateDrawPolylineVolume } from './maptools/polylineVolume';
import { getEntityPropertyBag } from './map-utils';
import { type UseQueryStoreResult, type MutationStoreResult } from '@sveltestack/svelte-query';
import { HTTPError } from 'ky';
import { toggleDrawDistanceLineOnMap, toggleDrawPolylineOnMap } from './maptools/polylineTool';
import { PermissionService } from '$lib/utils/permissions-helper';
import { deactivateTrenchSubdivisionTool } from '$lib/utils/polyline-splitting';
import type { IMastPointMinimalModel } from '$lib/models/IMastPointMinimalModel';
import { toggleQuickMoveMastpointPosition, toggleQuickMovePoleConstructionPosition, toggleQuickMovePolePosition } from './maptools/moveMastpoint';
import { deactivateRotationTool } from './maptools/rotateMastpoint';
import { mutations } from '$lib/data/mutations';
import { toggleQuickAdjustHeightMastpoint } from './maptools/adjustHeightTool';
import { cleanupPointToGroundTool, togglePointToGroundTool } from './maptools/pointToGroundTool';
import {
	cleanupMastpointsAlongLineTool,
	toggleMastpointsAlongLineTool
} from './maptools/mastpointsAlongLineTool';

/** Pre-filled pole height for the insert-mastpoint tool, in metres. */
export const INSERT_MASTPOINT_DEFAULT_POLE_HEIGHT_M = 10;

export interface ISectionInfoMast {
	id: number;
	mastFunction: string;
	mastFunctionName: string;
	operationalMarking: string;
}

export interface ISectionInfoLine {
	id: number;
}

export interface ISectionInfoSpanLineset {
	id: number;
	lines: ISectionInfoLine[];
}

export interface ISectionCircuitInfo {
	firstMastId: number;
	lastMastId: number;
	/**
	 * Per-circuit montage stretch as returned by `/info` and `/calculate`. Falls
	 * back to `MONTAGE_STRETCH_DEFAULT` in the UI when `montageStretchOnAllLines`
	 * is false (i.e. the value is not uniformly set across the circuit).
	 */
	montageStretch: number;
	/** True when `montageStretch` is set consistently across every line in the circuit. */
	montageStretchOnAllLines: boolean;
	conductorType: string;
	spanLinesets: ISectionInfoSpanLineset[];
}

export interface ISectionLoadCases {
    name: string;
    temperature: number;
    usesIceLoad: boolean;
    usesWindLoad: boolean;
}

export interface ISectionInfo {
    sectionLoadCases: ISectionLoadCases[];
	masts: ISectionInfoMast[];
	sectionCircuits: ISectionCircuitInfo[];
	loadCases?: ILoadCase[];
}

export interface ILoadCase {
	name: string;
	/**
	 * Group label this load case belongs to. The AreaToolContextMenu groups
	 * load cases by `parentName` and renders each group as a collapsible
	 * accordion — the parentName itself is not selectable, only the load
	 * cases under it are.
	 */
	parentName: string;
	temperature: number;
	usesIceLoad: boolean;
	usesWindLoad: boolean;
	czml: any[];
	calculations: ILoadCaseCalculations;
    isDefault: boolean;
	/**
	 * Per-load-case RGB triple (0–255 each) used by the panel's "Farg etter
	 * lasttilfelle" toggle. Optional — when missing or malformed (< 3 values)
	 * the line keeps its section-circuit colour.
	 */
	rgb?: [number, number, number] | number[] | null;
}

export interface ILoadCaseCalculations {
	poles: ILoadCasePoleCalc[];
	crossbeams: ILoadCaseCrossbeamCalc[];
	supportWires: ILoadCaseSupportWireCalc[];
	lines: ILoadCaseLineCalc[];
}

export interface ILoadCasePoleCalc {
	id: number;
	operationalMarking: string;
	position: string;
	material: string;
	diameter: number;
	visibleHeight: number;
	util: number;
	maxMoment: number;
	maxMomentHeight: number;
	displacementTop: number;
	bottomAxialForce: number;
	usedCaseName: string;
    worstCaseDiameter: number;
}

export interface ILoadCaseCrossbeamCalc {
	id: number;
	name: string;
	function: string;
	length: number;
	operationalMarkingMast: string;
	util: number;
	maxMomentX: number;
	maxMomentY: number;
	dimMomentCapacityX: number;
	dimMomentCapacityY: number;
	torsion: number;
	dimTorsionCapacity: number;
	offsetFromMastTop: number;
	usedCaseName: string;
}

export interface ILoadCaseSupportWireCalc {
	id: number;
	operationalMarkingMast: string;
	material: string;
	diameter: number;
	util: number;
	tension: number;
	ratedTensileStrength: number;
	bearingXY: number;
	bearingXZ: number;
	gradient: number;
	length: number;
	usedCaseName: string;
}

export interface ILoadCaseLineCalc {
	id: number;
	conductorType: string;
	voltage: number;
	operationalMarkingMast1: string;
	operationalMarkingMast2: string;
	util: number;
	tension: number;
	sag: number;
	lowestDistanceLineTerrain: number;
	utsPercentage: number;
	status: string;
	usedCaseName: string;
}

/** One sectionCircuit entry sent to `Section/calculate`. */
export interface ISectionCalculationCircuit {
	montageStretch: number;
	spanLinesets: { id: number }[];
}

/** Body of `Section/calculate`. */
export interface ISectionCalculationPayload {
    includeCalculationFile: boolean;
	/** The masts selected with the path tool. */
	mastIds: number[];
    distanceControlLineTerrain: boolean;
	sectionCircuits: ISectionCalculationCircuit[];
	loadCases: string[];
    planId?: number;
}

export type ToolPropertyKey =
| 'addMastPoint'
| 'insertMastPoint'
| 'addLinesNewLines'
//| 'moveExistingMastPoint'
| 'rotateMastpoint'
| 'createTerrainProfileFromPoles'
| 'removeSandboxEntities'
| 'addLabel'
| 'addLabel'
| 'addMarker'
| 'insertNewBox'
| 'drawPolygon'
| 'drawRectangle'
| 'drawMeasurementLine'
| 'drawPolyline'
| 'polylineSubdivision'
| 'drawPolylineVolume'
| 'editDrawnEntity'
| 'addEntityTypes'
| 'quickMoveMastPoint'
| 'quickMovePole'
| 'quickMovePoleConstruction'
| 'quickAdjustMastPointHeight'
| 'attachLineToLink'
| 'polygonSelection'
| 'imageAreaSelection'
| 'pointToGround'
| 'mastpointsAlongLine';


type HotKeys = {
	description: string;
	instructions: string[];
	leftClick?: string;
	leftDownMouseMove?: string;
	mouseMove?: string;
	rightClick?: string;
	shiftLeftClick?: string;
	ctrlLeftClick?: string;
	escape?: string;
}

interface ToolProperties {
	name: string;
	usage: HotKeys;
	toolEnabled: boolean;
	deactivateTool: () => void;
	deactivateToolText?: string;
	customQuery?: () => UseQueryStoreResult<Blob, HTTPError, number[]> | MutationStoreResult<any, HTTPError, any>;
	customQueryText?: string;
}

export interface IMapToolsStore {
	addMastPoint: ToolProperties & {
        mastpointTemplate: number | null
    };
	insertMastPoint: ToolProperties & {
		/** Height in metres of the pole to insert. Drives the preview and the payload. */
		poleHeight: number;
		mastpointTemplate: number | null;
	};
	addLinesNewLines: ToolProperties;
    attachLineToLink: ToolProperties;
	addMarker: ToolProperties & {
		markerColor: Color;
	};
	insertNewBox: ToolProperties & {
		uuid: string | undefined;
		boxColor: Color;
		dimensions: Cartesian3;
		boxWhichNeedsOM: string | undefined;
	};
	createTerrainProfileFromPoles: ToolProperties;
	removeSandboxEntities: ToolProperties;
	addLabel: ToolProperties & {
		labelText: string;
		labelTextColor: Color;
	};
	drawMeasurementLine: ToolProperties & {
		activeShapePoints: Cartesian3[];
		activeShape: undefined | Entity;
		floatingPoint: undefined | Entity;
		labelEntity: Entity | undefined;
		shouldClampToGround: boolean;
		lineId: string | undefined;
	};
	drawPolygon: ToolProperties & {
		polygonColor: Color;
		polygonEntity: Entity | null;
		polygonDatasource: CustomDataSource | undefined;
		polygonShapePoints: Cartesian3[];
		polygonDrawingMode: 'DRAWING_ONLY' | 'PROJECT_CREATION' | 'PROJECT_EDITING' | 'PLAN_CREATION' | 'PLAN_EDITING' | undefined;
	};
	drawPolyline: ToolProperties & {
		lineColor: Color;
		lineType: 'SOLID' | 'DASHED';
		activeShapePoints: Cartesian3[];
		activeShape: Entity | undefined;
		floatingPoint: Entity | undefined;
		cornerPoints: Entity[];
		lineId: string | undefined;
	};
    rotateMastpoint: ToolProperties & {
        payload: {
            mastId: number | undefined,
            bearingOffset: number | undefined
        };
        resetToolOnComplete: undefined | (() => void);
    };
	polylineSubdivision: ToolProperties & {
		subdivisions: Entity[],
		parentPath: string
	},
	drawPolylineVolume: ToolProperties &{
		isHighVoltage: boolean;
		entityStart: Entity | undefined;
		entityEnd: Entity | undefined;
	};
	editDrawnEntity: ToolProperties & {
		entityId: string;
	};
	drawRectangle: ToolProperties & {
		rectangleColor: Color;
	};
	addEntityTypes: ToolProperties & {
		uuid: string | undefined;
		type: 'kabelskap' | 'nettstasjon';
	}
	//moveExistingMastPoint: ToolProperties;
	quickMoveMastPoint: ToolProperties;
    quickMovePole: ToolProperties;
	quickMovePoleConstruction: ToolProperties;
	quickAdjustMastPointHeight: ToolProperties;
	polygonSelection: ToolProperties;
	imageAreaSelection: ToolProperties;
	pointToGround: ToolProperties;
	mastpointsAlongLine: ToolProperties & {
		/** Ids of the clicked mastpoints, in click order. */
		mastpointIds: number[];
	};
	// mapMode distanceAnalysis start
	mapStoreInputMode: 'default' | 'distanceMode';
	distanceAnalysisSettings: {
		terrainProfileOffsets: {
			offsetInMeters: number;
			color: [number, number, number];
			active: boolean;
		}[];
		lineDistanceMode: 'single' | 'horizontal' | 'all';
		loadOccurrence: 'normal'
	}
	// mapMode distanceAnalysis end
	selectedMapEntities: any[];
	selectedMapPoint: Cartesian3;
	newMastPointsCurrentlyProcessing: string[];
	displayVirtualSupports: boolean;
	displayPhysicalSupports: boolean;
	displayOperationalMarkingLabels: boolean;
}

const initialMapToolStore: IMapToolsStore = {
	addMastPoint: {
		name: 'Sett ut mastepunkt',
		usage: {
			description: "Klikk for å plassere mastepunkt i kartet. Mastepunkt vil bli lagret med firmaets standardverdier som høyde og type. Disse dataene kan justeres i etterkant.",
			instructions: ["Venstreklikk for å plassere mastepunkt.", "Flytt musepekeren i kartet for å dra en målelinje til neste mastepunkt."],
			leftClick: 'Venstreklikk for å plassere et mastepunkt'
		},
        mastpointTemplate: null,
		toolEnabled: false,
		deactivateTool: deactivateAddMastpointTool
	},
	insertMastPoint: {
		name: 'Sett inn bæremast',
		usage: {
			description:
				'Hold musepekeren mellom to tilkoblede mastepunkt for å se hvor det nye mastepunktet vil bli plassert. Mastepunktet plasseres i linjeretning mellom nabomastene og kobles automatisk til ledningene på begge sider.',
			instructions: [
				'Hold musepekeren mellom to mastepunkt, under ledningen, for å forhåndsvise plasseringen og avstanden til nabomastene.',
				'Venstreklikk for å sette inn mastepunktet.'
			],
			leftClick: 'Venstreklikk mellom to mastepunkt for å sette inn et nytt',
			escape: 'Esc avbryter verktøyet'
		},
		poleHeight: INSERT_MASTPOINT_DEFAULT_POLE_HEIGHT_M,
		mastpointTemplate: null,
		toolEnabled: false,
		// Registered by the tool itself when activated (see maptools/insertMastpoint.ts).
		deactivateTool: () => {}
	},
	addLinesNewLines: {
		name: 'Legg til ledninger',
		usage: {
			description: 'Opprett nye ledningsett mellom master og bakkepunkter. Velg komponenter eller mastepunkter i den rekkefølgend du vil henge opp de nye ledningene.',
			instructions: [
				'Venstreklikk for å selektere et bakkepunkt eller en stolpe/travers på mastepunktet',
				'Fortsett med valg av bakkepunkt/mastepunkt til traséen er ferdig oppmerket',
				'Bruk CTRL + venstreklikk for å avmarkere en mast eller et bakkepunkt',
				'Høyreklikk eller trykk avslutt når trasé er ferdig for å gå videre til valg av ledningsett.'
			],
			leftClick: 'Venstreklikk på mastepunkt eller terreng for å markere hvor du vil henge opp ledninger.',
			ctrlLeftClick: 'CTRL + venstreklikk for å avmarkere en mast eller et bakkepunkt.',
            rightClick: 'Høyreklikk for å bekrefte trasé og gå videre til valg av ledningsett.',
		},
		toolEnabled: false,
        deactivateToolText: 'Avbryt',
		deactivateTool: () => {}
	},
	addMarker: {
		name: 'Legg til markør',
		usage: {
			description: "Venstreklikk for å plassere markør.",
			instructions: ["Velg ev. markør-farge", "Trykk i kartet for å plassere markør."],
			leftClick: 'Venstreklikk for å plassere en markør.'
		},
		toolEnabled: false,
		markerColor: Color.ROYALBLUE.withAlpha(0.7),
		deactivateTool: toggleAddMarkerEntityToMap
	},
    attachLineToLink: {
        name: 'Koble ledning til oppheng',
        usage: {
            description: 'Venstreklikk på et ledningsoppheng og en ledning. Det spiller ingen rolle hvilke du velger først. Komponentene som er valgt vil bli koblet sammen.',
            instructions: ['Velg en ledning eller et ledningsoppheng i kartet.', 'Velg deretter det andre objektet for å koble ledningen til opphenget.'],
            leftClick: 'Venstreklikk for å velge'
        },
        toolEnabled: false,
        deactivateTool: () => {}
    },
	insertNewBox: {
		uuid: undefined,
		name: 'Plasser 3D-boks',
		usage: {
			description: "Plasser ut bokser / kuber med 3D-mål i kartet. Kan brukes for visualisering av nettstasjoner, ladestasjoner og andre objekter som er typiske ved prosjekteringsarbeid.",
			instructions: [
				"Fyll inn eventuell lengde, bredde og høyde på boks i meter.", 
				"Velg eventuell farge på boks. Standard hvit.", 
				"Flytt musepekeren i kartet for en forhåndsvisning av boksen.",
				"Hold inne venstre museknapp og dra i X-aksen for å rotere boksen.",
				"Trykk på venstre museknapp for å plassere boksen."
			],
			leftClick: 'Venstreklikk for å plassere boksen.',
			leftDownMouseMove: 'Holde venstre museknapp nede og beveg musen for å rotere boksen'
		},
		boxWhichNeedsOM: undefined,
		toolEnabled: false,
		boxColor: Color.WHITE.withAlpha(0.7),
		dimensions: new Cartesian3(10, 10, 10),
		deactivateTool: toggleAddBoxEntityToMap
	},
	addLabel: {
		name: 'Legg til tekst',
		usage: {
			description: "Venstreklikk for å plassere tekst i kartet.",
			instructions: ["Fyll inn ønsket tekst i tekstfeltet under", "Trykk i på ønsket plassering i kartet"],
			leftClick: 'Plasser tekst i kartet.',
		},
		toolEnabled: false,
		labelText: '',
		labelTextColor: Color.WHITE,
		deactivateTool: toggleAddLabelEntityToMap
	},
	removeSandboxEntities: {
		name: 'Slett tegninger',
		usage: {
			description: "Venstreklikk for å slette opptegnet objekt. Kan ikke angres.",
			instructions: [],
			leftClick: 'Venstreklikk på et opptegnet objekt for å fjerne det. NB: Dette er ikke mulig å angre.',
		},
		toolEnabled: false,
		deactivateTool: toggleRemoveEntities
	},
	createTerrainProfileFromPoles: {
		name: 'Tegn og eksporter terrengprofil',
		usage: {
			description: "Venstreklikk for å velge aktuelle stolper.",
			instructions: ["Venstreklikk på mastepunkt/stolper i kartet for å markere trasé", "Når traséen er ferdig opptegnet, trykk knappen Last ned linjeprofil for å eksportere terrengprofilen.", "Høyreklikk eller trykk Avslutt verktøy for å deaktivere. Dette fjerner også den opptegnede traversen og de-selekterer stolpene."],
			leftClick: 'Velg mastepunkt for å lage en trasé.',
			rightClick: 'Avslutt terrengprofilen.',
		},
		toolEnabled: false,
		deactivateTool: toggleSelectPolesForTerrainProfileCreation,
		customQuery: () => mutations.poles.useTerrainProfileMutation(),
        customQueryText: 'Last ned linjeprofil'
	},
	drawMeasurementLine: {
		name: 'Måleverktøy',
		usage: {
			description: "Venstreklikk for å lage et brekkpunkt på linjen. Høyreklikk for å avslutte linjen.",
			instructions: [
				"Start linjen ved å venstreklikke i kartet.", 
				"Beveg musepekeren for å trekke linjen.", 
				"Venstreklikk igjen for å starte et nytt segment på linjen.", 
				"Etter siste punkt er plassert, høyreklikk for å avslutte."
			],
			leftClick: 'Venstreklikk for å lage et brekkpunkt på linjen',
			rightClick: 'Høyreklikk for å avslutte linjen',
		},
		activeShapePoints: [],
		activeShape: undefined,
		floatingPoint: undefined,
		toolEnabled: false,
		shouldClampToGround: true,
		lineId: undefined,
		labelEntity: undefined,
		deactivateTool: toggleDrawDistanceLineOnMap
	},
	drawPolygon: {
		name: 'Områdeverktøy (Polygon)',
		usage: {
			description: "Venstreklikk for å markere et punkt i polygonet. Høyreklikk for å avslutte.",
			instructions: ["Venstreklikk for å lage et punkt i polygonet", "Fortsett for å lage flere punkter", "Høyreklikk eller trykk på Avslutt polygon for å avslutte polygonet. Polygonet må ha minst tre punkter."],
			leftClick: 'Lag et nytt punkt i polygonet',
			rightClick: 'Avslutt polygonet',
		},
		toolEnabled: false,
		polygonColor: Color.WHITE.withAlpha(0.7),
		polygonEntity: null,
		polygonDatasource: undefined,
		polygonShapePoints: [],
		polygonDrawingMode: undefined,
		deactivateTool: deactivatePolygonDrawingTool,
		deactivateToolText: 'Avslutt polygon'

	},
	drawPolyline: {
		name: 'Tegn polylinje',
		usage: {
			description: "Venstreklikk for å lage et brekkpunkt på linjen. Høyreklikk for å avslutte linjen.",
			instructions: [
				"Start linjen ved å venstreklikke i kartet.",
				"Beveg musepekeren for å trekke linjen.",
				"Venstreklikk igjen for å starte et nytt segment på linjen.",
				"Etter siste punkt er plassert, høyreklikk for å avslutte."
			],
			leftClick: 'Start linje eller lage et brekkpunkt på linjen',
			rightClick: 'Avslutt linjen',
		},
		toolEnabled: false,
		lineColor: Color.YELLOW,
		lineType: 'SOLID',
		activeShapePoints: [],
		activeShape: undefined,
		floatingPoint: undefined,
		cornerPoints: [],
		lineId: undefined,
		deactivateTool: toggleDrawPolylineOnMap
	},
	polylineSubdivision: {
		name: 'Lag delstrekning',
		usage: {
			instructions: [],
			description: 'Venstreklikk på et punkt i traséen hvor du ønsker å dele den inn i to strekninger',
			leftClick: 'Venstreklikk på et punkt i traséen hvor du ønsker å dele den inn i to strekninger',
			rightClick: 'Avslutt og lagre delstrekningene',
		},
		toolEnabled: false,
		subdivisions: [],
		parentPath: '',
		deactivateTool: deactivateTrenchSubdivisionTool
	},
	drawPolylineVolume: {
		name: 'Tegn Trasé',
		usage: {
			description: "Tegn en trasé i kartet mellom to entiteter. Velg et startpunkt (Entitet A) og tegn frem til et sluttpunkt (Entitet B). Traséen vil etter avslutning gjøres om til en grøft. Traséen markerer senter av grøften.",
			instructions: ['Venstreklikk på et tilknytningspunkt (Mast eller 3D boks)', 'Venstreklikk i kartet for å tegne opp trasé', 'Avslutt med å venstreklikk på avsluttende tilknytningspunkt'],
			leftClick: 'Venstreklikk for å lage punkter til trasé',
		},
		toolEnabled: false,
		isHighVoltage: false,
		entityStart: undefined,
		entityEnd: undefined,
		deactivateTool: deactivateDrawPolylineVolume
	},
	editDrawnEntity: {
		name: 'Edit drawn entity',
		usage: {
			description: "Venstreklikk på ønsket objekt for å redigere.",
			instructions: [],
		},
		toolEnabled: false,
		entityId: '',
		deactivateTool: toggleEditDrawnEntity
	},
	drawRectangle: {
		name: 'Tegn rektangel',
		usage: {
			description: "Tegn et rektangel i kartet.",
			instructions: ["Venstreklikk for å starte rektangelet", "Beveg musepekeren til du har ønsket størrelse og plassering av rektangelet", "Høyreklikk for å avslutte"],
			leftClick: 'Start rektangelet.',
			rightClick: 'Avslutt rektangelet.',
		},
		toolEnabled: false,
		rectangleColor: Color.WHITE.withAlpha(0.7),
		deactivateTool: toggleDrawRectangleOnMap
	},
    rotateMastpoint: {
        name: 'Roter mastepunkt',
        usage: {
            description: "Venstreklikk for å velge mastepunkt, velg roteringssirkelen for å rotere. Bekreft rotasjon for å avslutte.",
            instructions: ["Venstreklikk på et mastepunkt for å markere det", "Velg roteringssirkelen som vises rundt mastepunktet og dra for å rotere mastepunktet.", "Bekreft rotasjon for å avslutte."],
            leftClick: 'Venstreklikk for å markere ønsket mastepunkt du vil rotere.'
        },
        toolEnabled: false,
		customQuery: () => mutations.mastPoints.useUpdateMastpointRotationMutation(),
		customQueryText: 'Bekreft rotasjon',
        deactivateTool: deactivateRotationTool,
        resetToolOnComplete: undefined,
        payload: {
            mastId: undefined,
            bearingOffset: undefined
        }
    },
	addEntityTypes: {
		name: 'Legg til entiteter',
		usage: {
			description: "Venstreklikk for å plassere entitet i kartet.",
			instructions: [],
			leftClick: 'Venstreklikk for å plassere valgt entitet.',
		},
		toolEnabled: false,
		type: 'kabelskap',
		uuid: undefined,
		deactivateTool: toggleEntityTypesToMap
	},
	/*moveExistingMastPoint: {
		name: 'Flytt mastepunkt',
		usage: {
			description: "Venstreklikk i kartet for å flytte mastepunkt til posisjon.",
			instructions: ["Klikk i kartet for å flytte mastepunktet til ønsket posisjon."],
			leftClick: 'Bekreft ny posisjon.',
		},
		toolEnabled: false,
		deactivateTool: () => toggleMoveMastPointEntityInMap(null)
	},*/
	quickMoveMastPoint: {
		name: 'Flytt mastepunkt',
		usage: {
			description: "Velg og flytt mastepunkt til ny posisjon.",
			instructions: ["Velg mastepunkt i kartet", "Flytt musepeker til ønsket posisjon. Du vil få en forhåndsvisning av hvor mastepunktet vil bli plassert.", "Venstreklikk for å velge ny posisjon."],
			leftClick: 'Venstreklikk for å markere mastepunkt og flytte mastepunktet i kartet.',
		},
		toolEnabled: false,
		deactivateTool: toggleQuickMoveMastpointPosition
	},
    quickMovePole: {
		name: 'Flytt stolpe',
		usage: {
			description: "Velg et punkt i kartet hvor du vil flytte stolpen.",
			instructions: ["Flytt stolpe til ønsket posisjon. Du vil få en forhåndsvisning av hvor stolpen vil bli plassert.", "Venstreklikk for å velge ny posisjon."],
			leftClick: 'Venstreklikk for å flytte stolpen i kartet.',
		},
		toolEnabled: false,
		deactivateTool: toggleQuickMovePolePosition
	},
    quickMovePoleConstruction: {
        name: 'Flytt stolpekonstruksjon',
        usage: {
            description: "Velg et punkt i kartet hvor du vil flytte stolpekonstruksjonen.",
            instructions: ["Flytt stolpekonstruksjon til ønsket posisjon. Du vil få en forhåndsvisning av hvor stolpekonstruksjonen vil bli plassert.", "Venstreklikk for å velge ny posisjon."],
            leftClick: 'Venstreklikk for å flytte stolpekonstruksjonen i kartet.',
        },
        toolEnabled: false,
        deactivateTool: toggleQuickMovePoleConstructionPosition
    },
	quickAdjustMastPointHeight: {
		name: 'Endre høyde på mastepunkt',
		usage: {
			description: "Venstreklikk for å velge mastepunkt, beveg musepekeren for å endre høyde, høyreklikk for å avslutte.",
			instructions: ["Venstreklikk på et mastepunkt for å markere det", "Beveg musepekeren opp og ned for å justere høyden på mastepunktet.", "Høyreklikk for å avslutte."],
			leftClick: 'Venstreklikk for å markere ønsket mastepunkt du vil justere høyde på.',
			mouseMove: 'Beveg musepekeren opp og ned for å justere høyden på mastepunktet.',
			rightClick: 'Høyreklikk for å avslutte justering av høyde.',
		},
		toolEnabled: false,
		deactivateTool: toggleQuickAdjustHeightMastpoint
	},
	polygonSelection: {
		name: 'Velg entiteter i område',
		usage: {
			description: 'Tegn et polygon i kartet for å hente seksjonsinformasjon (mastepunkt og ledningssett) innenfor området.',
			instructions: [
				'Venstreklikk i kartet for å plassere hjørner i polygonet.',
				'Fortsett å klikke for å legge til flere punkter. Minimum tre punkter kreves.',
				'Høyreklikk for å avslutte polygonet og hente seksjonsinformasjon.'
			],
			leftClick: 'Venstreklikk for å plassere et hjørne i polygonet.',
			rightClick: 'Høyreklikk for å avslutte polygonet og hente seksjonsinformasjon.'
		},
		toolEnabled: false,
		deactivateTool: () => {}
	},
	imageAreaSelection: {
		name: 'Bilder i område',
		usage: {
			description:
				'Tegn et polygon i kartet for å hente bilder tatt innenfor området. Bildene vises som markører i kartet.',
			instructions: [
				'Venstreklikk i kartet for å plassere hjørner i polygonet.',
				'Fortsett å klikke for å legge til flere punkter. Minimum tre punkter kreves.',
				'Høyreklikk for å avslutte polygonet og hente bilder.'
			],
			leftClick: 'Venstreklikk for å plassere et hjørne i polygonet.',
			rightClick: 'Høyreklikk for å avslutte polygonet og hente bilder.'
		},
		toolEnabled: false,
		deactivateTool: () => {}
	},
	pointToGround: {
		name: 'Punkt til bakke',
		usage: {
			description: 'Klikk på et punkt i punktskyen for å måle vertikal høyde ned til terrenget.',
			instructions: [
				'Beveg musepekeren for forhåndsvisning.',
				'Venstreklikk for å låse målingen.',
				'Høyreklikk eller Esc for å avslutte.'
			],
			leftClick: 'Venstreklikk for å måle',
			rightClick: 'Høyreklikk for å avslutte'
		},
		toolEnabled: false,
		deactivateTool: togglePointToGroundTool
	},
	mastpointsAlongLine: {
		name: 'Mastepunkt langs linje',
		usage: {
			description: 'Velg mastepunkt langs en felles linje. Kun mastepunktene som er markert kan velges.',
			instructions: [
				'Venstreklikk på et mastepunkt for å starte utvalget.',
				'Nabomastene som kan velges markeres med et plusstegn. Velger du en mast lenger ut, velges alle mastene mellom også.',
				'Ctrl + venstreklikk på et valgt mastepunkt fjerner det og alle valgt etter det.',
				'Høyreklikk for å fullføre og hente seksjonsinformasjon (minst 2 mastepunkt). Esc avbryter og fjerner utvalget.'
			],
			leftClick: 'Venstreklikk på et mastepunkt for å velge det',
			ctrlLeftClick: 'Ctrl + venstreklikk for å fjerne et valgt mastepunkt',
			rightClick: 'Høyreklikk for å fullføre og hente seksjonsinformasjon'
		},
		mastpointIds: [],
		toolEnabled: false,
		deactivateTool: toggleMastpointsAlongLineTool
	},
	mapStoreInputMode: 'default',
	distanceAnalysisSettings: {
		terrainProfileOffsets: [
		{
			offsetInMeters: 4,
			color: [42, 96, 37],
			active: false
		},
		{
			offsetInMeters: 6,
			color: [222, 22, 22],
			active: false
		}],
		lineDistanceMode: 'horizontal',
		loadOccurrence: 'normal',
	},
	selectedMapEntities: [],
	selectedMapPoint: new Cartesian3(),
	newMastPointsCurrentlyProcessing: [],
	displayVirtualSupports: true,
	displayPhysicalSupports: true,
	displayOperationalMarkingLabels: false
};

function createMapToolsStore() {
	const { subscribe, set, update } = writable<IMapToolsStore>(cloneDeep(initialMapToolStore));

	return {
		update,
		set,
		subscribe,
		reset: () =>
			update((store) => {
				store = cloneDeep(initialMapToolStore);
				return store;
			}),
		setSelectedMapEntities: (entities: Entity[], merge = false) =>
			update((store) => {
				let selectedMapEntities = [];
				if (merge) {
					selectedMapEntities = [...store.selectedMapEntities, ...entities];
				} else {
					selectedMapEntities = entities;
				}

				// Avoids duplicates based on entity ID
				(store.selectedMapEntities as any[]) = [
					...new Map(selectedMapEntities.map((entity) => [entity.id, entity])).values()
				];
				return store;
			}),
		setNewBoxEntity: (uuid: string | undefined, collision: string = '') => {
			update((store) => {
				const previousBoxId = store.insertNewBox.uuid;
				store.insertNewBox.uuid = uuid ? `sandbox_box:${uuid}` : undefined;
				if(previousBoxId && !uuid && collision !== 'ui' && PermissionService.can('UNDERGROUND')){
					store.insertNewBox.boxWhichNeedsOM = previousBoxId;
				}
				return store;
			});
		},
		setNewEntityType: (uuid: string | undefined) =>
			update((store) => {
				store.addEntityTypes.uuid = uuid ? `${store.addEntityTypes.type}:${uuid}` : undefined;
				return store;
			}),
		setCurrentPolygon: (entity: Entity) => 
			update((store) => {
				store.drawPolygon.polygonEntity = entity ?? null;
				return store;
			}),
		/**
		 * Pass a single object of type `Entity` (poles, lines, etc.) to
		 * be deselected/removed from the `selectedMapEntities` array.
		 */
		deselectSpecificMapEntity: (entity: Entity) =>
			update((store) => {
				store.selectedMapEntities = store.selectedMapEntities.filter((_entity: Entity) => {
					const { data: entityData } =
						getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(entity);
					const { data: _entityData } =
						getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(_entity);
					if (entityData.id !== _entityData.id) {
						return entity;
					}
				});

				return store;
			}),
		resetSelectedMapEntities: () =>
			update((store) => {
				(store.selectedMapEntities as Entity[]) = [];
				return store;
			}),
		resetMapToolsStore: (viewer: Viewer) =>
			update((store) => {
				store.selectedMapEntities = [];
				store.addMastPoint.toolEnabled = false;
                store.addMastPoint.mastpointTemplate = null;
				store.removeSandboxEntities.toolEnabled = false;
				//store.moveExistingMastPoint.toolEnabled = false;
				store.createTerrainProfileFromPoles.toolEnabled = false;

				store.editDrawnEntity.toolEnabled = false;
				store.editDrawnEntity.entityId = '';
				//removeAllScreenSpaceInputActions(viewer, viewer.screenSpaceEventHandler);

				store.drawMeasurementLine.toolEnabled = false;
				store.drawMeasurementLine.shouldClampToGround = true;
				store.drawMeasurementLine.activeShapePoints = [];
				store.drawMeasurementLine.activeShape = undefined;
				store.drawMeasurementLine.floatingPoint = undefined;
				store.drawMeasurementLine.lineId = undefined;
				store.drawMeasurementLine.labelEntity = undefined;

                store.rotateMastpoint.toolEnabled = false;

				store.addMarker.toolEnabled = false;
				store.addMarker.markerColor = Color.ROYALBLUE.withAlpha(0.7);

				store.insertNewBox.uuid = undefined;
				store.insertNewBox.toolEnabled = false;

				store.drawRectangle.toolEnabled = false;
				store.drawRectangle.rectangleColor = Color.WHITE.withAlpha(0.7);
				
				store.drawPolygon.toolEnabled = false;
				store.drawPolygon.polygonEntity = null;

				store.addLabel.toolEnabled = false;
				store.addLabel.labelText = '';
				store.addLabel.labelTextColor = Color.WHITE;

				store.drawPolyline.toolEnabled = false;
				store.drawPolyline.activeShapePoints = [];
				store.drawPolyline.activeShape = undefined;
				store.drawPolyline.floatingPoint = undefined;
				store.drawPolyline.cornerPoints = [];
				store.drawPolyline.lineId = undefined;

				store.drawPolylineVolume.toolEnabled = false;
				store.drawPolylineVolume.isHighVoltage = false;
				store.drawPolylineVolume.entityStart = undefined;
				store.drawPolylineVolume.entityEnd = undefined;

				store.addEntityTypes.toolEnabled = false;
				store.addEntityTypes.uuid = undefined;

				store.quickMoveMastPoint.toolEnabled = false;
                store.quickMovePole.toolEnabled = false;
				store.quickAdjustMastPointHeight.toolEnabled = false;
                store.quickMovePoleConstruction.toolEnabled = false;

				store.polygonSelection.toolEnabled = false;
				store.imageAreaSelection.toolEnabled = false;
				store.pointToGround.toolEnabled = false;
				store.mastpointsAlongLine.toolEnabled = false;
				store.mastpointsAlongLine.mastpointIds = [];

				return store;
			}),
		disableAllTools: (viewer: Viewer) =>
			update((store) => {
				if (store.addMastPoint.toolEnabled) {
					//Proper handling of deactivation
					deactivateAddMastpointTool();
				}
				store.addMastPoint.toolEnabled = false;
                store.addMastPoint.mastpointTemplate = null;
				if (store.insertMastPoint.toolEnabled) {
					// The tool registers its own deactivate function, which also resets this entry.
					store.insertMastPoint.deactivateTool();
				}
				store.insertMastPoint.toolEnabled = false;
				store.insertMastPoint.poleHeight = INSERT_MASTPOINT_DEFAULT_POLE_HEIGHT_M;
				store.insertMastPoint.mastpointTemplate = null;
				store.addLinesNewLines.toolEnabled = false;
                store.attachLineToLink.toolEnabled = false;
				store.removeSandboxEntities.toolEnabled = false;
				//store.moveExistingMastPoint.toolEnabled = false;
				store.createTerrainProfileFromPoles.toolEnabled = false;

				store.addLabel.toolEnabled = false;
				store.addLabel.labelText = '';
				store.addLabel.labelTextColor = Color.WHITE;

                store.rotateMastpoint.toolEnabled = false;

				store.editDrawnEntity.toolEnabled = false;
				store.editDrawnEntity.entityId = '';
				removeAllDrawnPolylineDragHandlesForEditablePolyline();
				removeAllScreenSpaceInputActions(viewer, viewer.screenSpaceEventHandler);

				store.drawMeasurementLine.toolEnabled = false;
				store.drawMeasurementLine.shouldClampToGround = true;
				store.drawMeasurementLine.activeShapePoints = [];
				store.drawMeasurementLine.activeShape = undefined;
				store.drawMeasurementLine.floatingPoint = undefined;
				store.drawMeasurementLine.lineId = undefined;
				store.drawMeasurementLine.labelEntity = undefined;

				store.addMarker.toolEnabled = false;
				store.addMarker.markerColor = Color.ROYALBLUE.withAlpha(0.7);

				store.insertNewBox.uuid = undefined;
				store.insertNewBox.toolEnabled = false;

				store.drawRectangle.toolEnabled = false;
				store.drawRectangle.rectangleColor = Color.WHITE.withAlpha(0.7);

				store.drawPolygon.toolEnabled = false;
				store.drawPolygon.polygonEntity = null;
				store.drawPolygon.polygonDatasource = undefined;
				store.drawPolygon.polygonShapePoints = [];
				store.drawPolygon.polygonDrawingMode = undefined;

				store.drawPolyline.toolEnabled = false;
				store.drawPolyline.activeShapePoints = [];
				store.drawPolyline.activeShape = undefined;
				store.drawPolyline.floatingPoint = undefined;
				store.drawPolyline.cornerPoints = [];
				store.drawPolyline.lineId = undefined;
				
				store.drawPolylineVolume.toolEnabled = false;
				store.drawPolylineVolume.isHighVoltage = false;
				store.drawPolylineVolume.entityStart = undefined;
				store.drawPolylineVolume.entityEnd = undefined;

				store.addEntityTypes.toolEnabled = false;
				store.addEntityTypes.uuid = undefined;

				store.quickMoveMastPoint.toolEnabled = false;
                store.quickMovePole.toolEnabled = false;
				store.quickAdjustMastPointHeight.toolEnabled = false;
                store.quickMovePoleConstruction.toolEnabled = false;

				store.polygonSelection.toolEnabled = false;
				store.imageAreaSelection.toolEnabled = false;

				if (store.pointToGround.toolEnabled) {
					// Turned off from outside (side menu or another tool): remove the hover label and Esc listener.
					cleanupPointToGroundTool();
				}
				store.pointToGround.toolEnabled = false;

				if (store.mastpointsAlongLine.toolEnabled) {
					// Turned off from outside (side menu or another tool): remove highlights and the Esc listener.
					cleanupMastpointsAlongLineTool();
				}
				store.mastpointsAlongLine.toolEnabled = false;
				store.mastpointsAlongLine.mastpointIds = [];

				return store;
			}),
		disableAllToolsExceptToggleToolByName: (
			toolName: ToolPropertyKey,
			viewer: Viewer
		) =>
			update((store) => {
				const previousStoreToolEnabledState = store[toolName].toolEnabled;

				if (store.addMastPoint.toolEnabled) {
					//Proper handling of deactivation
					deactivateAddMastpointTool();
				}
				store.addMastPoint.toolEnabled = false;
                store.addMastPoint.mastpointTemplate = null;
				if (store.insertMastPoint.toolEnabled) {
					// The tool registers its own deactivate function, which also resets this entry.
					store.insertMastPoint.deactivateTool();
				}
				store.insertMastPoint.toolEnabled = false;
				store.insertMastPoint.poleHeight = INSERT_MASTPOINT_DEFAULT_POLE_HEIGHT_M;
				store.insertMastPoint.mastpointTemplate = null;
                store.addLinesNewLines.toolEnabled = false;
                store.attachLineToLink.toolEnabled = false;
				store.removeSandboxEntities.toolEnabled = false;
				//store.moveExistingMastPoint.toolEnabled = false;
				store.createTerrainProfileFromPoles.toolEnabled = false;

                store.rotateMastpoint.toolEnabled = false;

				store.editDrawnEntity.toolEnabled = false;
				store.editDrawnEntity.entityId = '';
				removeAllDrawnPolylineDragHandlesForEditablePolyline();
				removeAllScreenSpaceInputActions(viewer, viewer.screenSpaceEventHandler);

				store.addLabel.toolEnabled = false;
				store.addLabel.labelText = '';
				store.addLabel.labelTextColor = Color.WHITE;

				store.drawMeasurementLine.toolEnabled = false;

				store.addMarker.toolEnabled = false;
				store.addMarker.markerColor = Color.ROYALBLUE.withAlpha(0.7);

				store.insertNewBox.toolEnabled = false;

				store.drawRectangle.toolEnabled = false;
				store.drawRectangle.rectangleColor = Color.WHITE.withAlpha(0.7);

				store.drawPolygon.toolEnabled = false;

				store.drawPolyline.toolEnabled = false;

				store.drawPolylineVolume.toolEnabled = false;
				store.drawPolylineVolume.isHighVoltage = false;
				store.drawPolylineVolume.entityStart = undefined;
				store.drawPolylineVolume.entityEnd = undefined;

				store.addEntityTypes.toolEnabled = false;
				store.addEntityTypes.uuid = undefined;

				store.quickMoveMastPoint.toolEnabled = false;
                store.quickMovePole.toolEnabled = false;
				store.quickAdjustMastPointHeight.toolEnabled = false;
                store.quickMovePoleConstruction.toolEnabled = false;

				store.polygonSelection.toolEnabled = false;
				store.imageAreaSelection.toolEnabled = false;

				if (store.pointToGround.toolEnabled) {
					// Turned off from outside (side menu or another tool): remove the hover label and Esc listener.
					cleanupPointToGroundTool();
				}
				store.pointToGround.toolEnabled = false;

				if (store.mastpointsAlongLine.toolEnabled) {
					cleanupMastpointsAlongLineTool();
				}
				store.mastpointsAlongLine.toolEnabled = false;
				store.mastpointsAlongLine.mastpointIds = [];

				store[toolName].toolEnabled = !previousStoreToolEnabledState;
				
				return store;
			})
	};
}

export const mapToolsStore = createMapToolsStore();

export const isAnyToolEnabled: Readable<boolean> = derived(mapToolsStore, ($mapToolsStore) => {
    const properties = Object.keys($mapToolsStore) as ToolPropertyKey[];
	return properties.some((property) => $mapToolsStore[property].toolEnabled);
});

export const selectedMapEntities: Readable<Entity[]> = derived(mapToolsStore, ($mapToolsStore) => $mapToolsStore.selectedMapEntities);

export const activeTool: Readable<ToolPropertyKey | undefined> = derived([isAnyToolEnabled, mapToolsStore], ([$isAnyToolEnabled, $mapToolsStore]) => {
	if($isAnyToolEnabled) {
		const properties = Object.keys($mapToolsStore) as ToolPropertyKey[];
		const enabledTool = properties.find((property) => $mapToolsStore[property].toolEnabled);
		return enabledTool;
	} 
	return undefined;
});
